import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { 
  MessageCircle, AlertCircle, CheckCircle, Clock,
  User, Mail, RefreshCw, Send, Eye, ArrowLeft,
  FileText, ChevronRight, X
} from 'lucide-react';
import '../styles/Admin.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminChatQuestions = () => {
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('escalated'); // escalated, incomplete
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Escalated questions state
  const [questions, setQuestions] = useState([]);
  const [questionStats, setQuestionStats] = useState({});
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [responding, setResponding] = useState(false);
  
  // Incomplete intakes state
  const [incompleteIntakes, setIncompleteIntakes] = useState([]);
  const [intakeStats, setIntakeStats] = useState({});
  const [selectedIntake, setSelectedIntake] = useState(null);

  const fetchEscalatedQuestions = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/chatbot/admin/escalated?status=pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch questions');
      const data = await response.json();
      setQuestions(data.questions || []);
      setQuestionStats({ pending: data.pending_count, total: data.total });
    } catch (err) {
      console.error('Fetch questions error:', err);
    }
  }, [token]);

  const fetchChatbotStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/chatbot/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setQuestionStats(prev => ({ ...prev, ...data }));
      }
    } catch (err) {
      console.error('Fetch chatbot stats error:', err);
    }
  }, [token]);

  const fetchIncompleteIntakes = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/intake-followup/admin/incomplete`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch incomplete intakes');
      const data = await response.json();
      setIncompleteIntakes(data.drafts || []);
    } catch (err) {
      console.error('Fetch incomplete intakes error:', err);
    }
  }, [token]);

  const fetchIntakeStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/intake-followup/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setIntakeStats(data);
      }
    } catch (err) {
      console.error('Fetch intake stats error:', err);
    }
  }, [token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchEscalatedQuestions(),
        fetchChatbotStats(),
        fetchIncompleteIntakes(),
        fetchIntakeStats()
      ]);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchEscalatedQuestions, fetchChatbotStats, fetchIncompleteIntakes, fetchIntakeStats]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isAuthenticated, navigate, fetchData]);

  const handleRespond = async () => {
    if (!selectedQuestion || !responseText.trim()) return;
    
    setResponding(true);
    try {
      const response = await fetch(`${API_URL}/api/chatbot/admin/respond`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          question_id: selectedQuestion.id,
          response: responseText,
          responder_name: `${user?.first_name} ${user?.last_name}`
        })
      });

      if (!response.ok) throw new Error('Failed to submit response');

      // Update local state
      setQuestions(prev => prev.filter(q => q.id !== selectedQuestion.id));
      setSelectedQuestion(null);
      setResponseText('');
      
      // Refresh stats
      fetchChatbotStats();
    } catch (err) {
      alert('Failed to submit response: ' + err.message);
    } finally {
      setResponding(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSectionName = (index) => {
    const sections = [
      'Personal Information',
      'Medical History',
      'Current Symptoms',
      'Medications',
      'Previous Treatments',
      'Lifestyle',
      'Goals & Expectations'
    ];
    return sections[index] || `Section ${index + 1}`;
  };

  if (!isAuthenticated) return null;

  if (error) {
    return (
      <div className="admin-container">
        <div className="admin-error">
          <AlertCircle size={48} />
          <h2>Error Loading Data</h2>
          <p>{error}</p>
          <button onClick={fetchData} className="admin-btn primary">Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page-wrapper">
      <div className="admin-container">
        <div className="admin-header">
          <div className="admin-header-content">
            <div className="admin-nav-links">
              <Link to="/" className="admin-logo-link">
                <img src="/cadence-logo.png" alt="CADENCE" className="admin-logo" />
              </Link>
              <div className="admin-breadcrumb">
                <Link to="/admin" className="admin-back-link">
                  <ArrowLeft size={16} />
                  Dashboard
                </Link>
              </div>
            </div>
            <h1>Visitor Inquiries & Incomplete Intakes</h1>
            <p className="admin-subtitle">
              Review chatbot questions and follow up on incomplete intake forms
            </p>
          </div>
          <div className="admin-header-actions">
            <button onClick={fetchData} className="admin-btn secondary">
              <RefreshCw size={16} className={loading ? 'spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

      {/* Stats Overview */}
      <div className="admin-stats-grid">
        <div className="admin-stat-card highlight">
          <div className="stat-icon pending">
            <MessageCircle size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{questionStats.pending_escalations || 0}</span>
            <span className="stat-label">Pending Questions</span>
          </div>
        </div>
        
        <div className="admin-stat-card">
          <div className="stat-icon messages">
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{questionStats.today_sessions || 0}</span>
            <span className="stat-label">Chats Today</span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon active">
            <FileText size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{intakeStats.total_incomplete_intakes || 0}</span>
            <span className="stat-label">Incomplete Intakes</span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon weekly">
            <Mail size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{(intakeStats.followups_72hr_sent || 0) + (intakeStats.followups_7day_sent || 0)}</span>
            <span className="stat-label">Follow-ups Sent</span>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="message-filters" style={{ marginBottom: '1.5rem' }}>
        <button 
          className={`filter-tab ${activeTab === 'escalated' ? 'active' : ''}`}
          onClick={() => setActiveTab('escalated')}
        >
          <MessageCircle size={16} />
          Chatbot Questions
          {questionStats.pending_escalations > 0 && (
            <span className="badge">{questionStats.pending_escalations}</span>
          )}
        </button>
        <button 
          className={`filter-tab ${activeTab === 'incomplete' ? 'active' : ''}`}
          onClick={() => setActiveTab('incomplete')}
        >
          <FileText size={16} />
          Incomplete Intakes
          {intakeStats.total_incomplete_intakes > 0 && (
            <span className="badge">{intakeStats.total_incomplete_intakes}</span>
          )}
        </button>
      </div>

      {/* Content Area */}
      {activeTab === 'escalated' && (
        <div className="admin-content-grid">
          {/* Questions List */}
          <div className="admin-card">
            <h3>Pending Questions</h3>
            {loading ? (
              <div className="table-loading">
                <RefreshCw className="spin" size={24} />
                <p>Loading...</p>
              </div>
            ) : questions.length === 0 ? (
              <div className="table-empty">
                <CheckCircle size={48} />
                <h3>All Caught Up!</h3>
                <p>No pending questions from visitors</p>
              </div>
            ) : (
              <div className="question-list">
                {questions.map(q => (
                  <div 
                    key={q.id}
                    className={`question-item ${selectedQuestion?.id === q.id ? 'selected' : ''}`}
                    onClick={() => setSelectedQuestion(q)}
                  >
                    <div className="question-content">
                      <p className="question-text">"{q.question}"</p>
                      <div className="question-meta">
                        {(q.user_name || q.name) && <span><User size={12} /> {q.user_name || q.name}</span>}
                        {(q.user_email || q.email) && <span><Mail size={12} /> {q.user_email || q.email}</span>}
                        <span><Clock size={12} /> {formatDate(q.created_at)}</span>
                        {q.page_context && <span><Eye size={12} /> {q.page_context}</span>}
                        {q.interest && <span className="badge" style={{ background: '#D4AF37', color: '#2D0A31' }}>Interested: {q.interest}</span>}
                      </div>
                    </div>
                    <ChevronRight size={18} className="chevron" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Response Panel */}
          <div className="admin-card">
            <h3>Respond to Question</h3>
            {selectedQuestion ? (
              <div className="response-panel">
                <div className="question-detail">
                  <label>Question:</label>
                  <p>"{selectedQuestion.question}"</p>
                </div>
                
                {selectedQuestion.user_email && (
                  <div className="visitor-info">
                    <label>From:</label>
                    <p>{selectedQuestion.user_name || 'Anonymous'} ({selectedQuestion.user_email})</p>
                  </div>
                )}

                <div className="response-form">
                  <label>Your Response:</label>
                  <textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    placeholder="Type your personalized response..."
                    rows={5}
                  />
                  <div className="response-actions">
                    <button 
                      className="admin-btn primary"
                      onClick={handleRespond}
                      disabled={!responseText.trim() || responding}
                    >
                      {responding ? (
                        <>
                          <RefreshCw className="spin" size={16} />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={16} />
                          Send Response
                        </>
                      )}
                    </button>
                    {selectedQuestion.user_email && (
                      <a 
                        href={`mailto:${selectedQuestion.user_email}?subject=Your Question to CADENCE&body=Hi ${selectedQuestion.user_name?.split(' ')[0] || 'there'},%0D%0A%0D%0AThank you for reaching out! You asked:%0D%0A"${selectedQuestion.question}"%0D%0A%0D%0A`}
                        className="admin-btn secondary"
                      >
                        <Mail size={16} />
                        Email Directly
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="table-empty">
                <MessageCircle size={48} />
                <h3>Select a Question</h3>
                <p>Click on a question from the list to respond</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'incomplete' && (
        <div className="admin-card">
          <div className="card-header">
            <h3>Incomplete Intake Forms</h3>
            <div className="intake-followup-stats">
              <span className="stat-badge">
                72hr sent: {intakeStats.followups_72hr_sent || 0}
              </span>
              <span className="stat-badge">
                7-day sent: {intakeStats.followups_7day_sent || 0}
              </span>
            </div>
          </div>
          
          {loading ? (
            <div className="table-loading">
              <RefreshCw className="spin" size={24} />
              <p>Loading...</p>
            </div>
          ) : incompleteIntakes.length === 0 ? (
            <div className="table-empty">
              <CheckCircle size={48} />
              <h3>No Incomplete Intakes</h3>
              <p>All users have either completed or not started their intake forms</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th>Progress</th>
                    <th>Last Updated</th>
                    <th>Follow-ups</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {incompleteIntakes.map((intake) => (
                    <tr key={intake.user_id}>
                      <td className="name-cell">
                        <span className="primary">{intake.user_name?.trim() || 'Unknown'}</span>
                        <span className="secondary">{intake.user_email}</span>
                      </td>
                      <td>
                        <div className="progress-bar-container">
                          <div 
                            className="progress-bar-fill"
                            style={{ width: `${((intake.completed_sections?.length || 0) / 7) * 100}%` }}
                          />
                          <span className="progress-text">
                            {intake.completed_sections?.length || 0}/7 sections
                          </span>
                        </div>
                      </td>
                      <td className="date-cell">
                        {formatDate(intake.last_updated)}
                      </td>
                      <td>
                        <div className="followup-badges">
                          {intake.followup_72hr_sent && (
                            <span className="badge success" title="72hr email sent">72hr ✓</span>
                          )}
                          {intake.followup_7day_sent && (
                            <span className="badge success" title="7-day email sent">7d ✓</span>
                          )}
                          {!intake.followup_72hr_sent && !intake.followup_7day_sent && (
                            <span className="badge default">Pending</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <button 
                          className="admin-btn small primary"
                          onClick={() => setSelectedIntake(intake)}
                        >
                          <Eye size={14} />
                          View Draft
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Intake Draft Modal */}
      {selectedIntake && (
        <div className="modal-overlay" onClick={() => setSelectedIntake(null)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Draft Intake: {selectedIntake.user_name?.trim() || 'Unknown'}</h2>
              <button className="modal-close" onClick={() => setSelectedIntake(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="intake-progress-overview">
                <h4>Sections Completed:</h4>
                <div className="section-progress-grid">
                  {[0, 1, 2, 3, 4, 5, 6].map(idx => (
                    <div 
                      key={idx}
                      className={`section-item ${selectedIntake.completed_sections?.includes(idx) ? 'complete' : 'incomplete'}`}
                    >
                      {selectedIntake.completed_sections?.includes(idx) ? (
                        <CheckCircle size={16} />
                      ) : (
                        <Clock size={16} />
                      )}
                      <span>{getSectionName(idx)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="intake-draft-data">
                <h4>Draft Data:</h4>
                {selectedIntake.form_data && Object.keys(selectedIntake.form_data).length > 0 ? (
                  <div className="draft-fields">
                    {Object.entries(selectedIntake.form_data).map(([key, value]) => (
                      <div key={key} className="draft-field">
                        <label>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</label>
                        <p>{typeof value === 'object' ? JSON.stringify(value) : value || 'Not provided'}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="empty-text">No data saved yet</p>
                )}
              </div>
            </div>
            <div className="modal-footer">
              {selectedIntake.user_email && (
                <a 
                  href={`mailto:${selectedIntake.user_email}?subject=Continue Your CADENCE Health Intake&body=Hi ${selectedIntake.user_name?.split(' ')[0] || 'there'},%0D%0A%0D%0AWe noticed you started filling out your health intake form but haven't finished yet.%0D%0A%0D%0AYour draft is saved and waiting for you. Is there anything we can help with?%0D%0A%0D%0AContinue here: https://foil-gold-med.preview.emergentagent.com/portal%0D%0A%0D%0ABest,%0D%0AThe CADENCE Team`}
                  className="admin-btn primary"
                >
                  <Mail size={16} />
                  Send Personal Follow-up
                </a>
              )}
              <button 
                className="admin-btn secondary"
                onClick={() => setSelectedIntake(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AdminChatQuestions;
